import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const planParam = sp.get("plan");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success(t("auth.welcomeBack"));
      nav(planParam ? `/app/abonament?plan=${planParam}` : "/app/oferte");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth.loginError"));
    } finally {
      setBusy(false);
    }
  };

  const sendForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: forgotEmail, origin_url: window.location.origin });
      setForgotSent(true);
      toast.success(r.data.message || t("auth.checkEmail"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0B111D]">
      <div className="hidden lg:block relative bg-[#111C30] text-white p-12">
        <Link to="/" className="flex items-center gap-3 font-display text-2xl font-extrabold tracking-tight">
          <img src="/brand/logo-icon.png" alt="IronOfferAI" className="w-11 h-11 rounded-lg"/>
          IronOfferAI
        </Link>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="overline" style={{color: "#FBBF24"}}>{t("auth.sideOverlineLogin")}</div>
          <h2 className="font-display text-4xl font-extrabold mt-4">{t("auth.sideTitleLogin")}</h2>
          <p className="mt-4 text-white/60">{t("auth.sideDescLogin")}</p>
        </div>
        <div className="absolute top-12 right-12 w-24 h-24 bg-[#00BFFF]"/>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-16 relative">
        <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
        {forgot ? (
          <div className="w-full max-w-md fade-up" data-testid="forgot-form">
            <div className="overline mb-3">{t("auth.resetOverline")}</div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("auth.forgotTitle")}</h1>
            {forgotSent ? (
              <div className="mt-6">
                <div className="p-4 bg-[#22C55E]/10 border-l-2 border-[#22C55E] text-sm text-[#CBD5E1]" data-testid="forgot-sent-msg">
                  {t("auth.forgotSentMsg", { email: forgotEmail })}
                </div>
                <button onClick={() => { setForgot(false); setForgotSent(false); }} className="btn-outline w-full mt-6" data-testid="back-to-login">{t("auth.backToLogin")}</button>
              </div>
            ) : (
              <>
                <p className="mt-2 text-sm text-[#94A3B8]">{t("auth.forgotDesc")}</p>
                <form onSubmit={sendForgot} className="mt-8 space-y-4">
                  <div>
                    <label className="overline block mb-2">{t("auth.emailLabel")}</label>
                    <input data-testid="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="input-flat" placeholder={t("common.emailPlaceholder")} />
                  </div>
                  <button type="submit" disabled={forgotBusy} className="btn-primary w-full disabled:opacity-50" data-testid="forgot-submit">
                    {forgotBusy ? t("auth.forgotSending") : t("auth.forgotSubmit")}
                  </button>
                  <button type="button" onClick={() => setForgot(false)} className="text-sm text-[#94A3B8] hover:text-[#00BFFF] w-full text-center" data-testid="forgot-cancel">{t("auth.backToLogin")}</button>
                </form>
              </>
            )}
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="login-form">
          <div className="overline mb-3">{t("auth.loginOverline")}</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("auth.loginTitle")}</h1>
          <p className="mt-2 text-sm text-[#94A3B8]">{t("auth.noAccount")} <Link to={planParam ? `/register?plan=${planParam}` : "/register"} className="text-[#00BFFF] font-semibold hover:underline">{t("auth.createFree")}</Link></p>
          {planParam && (
            <div className="mt-4 p-3 bg-[#FBBF24]/10 border-l-2 border-[#FBBF24] text-sm" data-testid="plan-hint">
              ⚡ {t("auth.planHintLogin", { plan: planParam })}
            </div>
          )}
          <div className="mt-8 space-y-4">
            <div>
              <label className="overline block mb-2">{t("auth.emailLabel")}</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="input-flat" placeholder={t("common.emailPlaceholder")}/>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">{t("auth.passwordLabel")}</label>
                <button type="button" onClick={() => { setForgot(true); setForgotEmail(email); }} className="text-xs text-[#00BFFF] hover:underline font-semibold" data-testid="forgot-link">{t("auth.forgotQuestion")}</button>
              </div>
              <input data-testid="login-password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="input-flat" placeholder="••••••••"/>
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full mt-8 disabled:opacity-50" data-testid="login-submit">
            {busy ? t("auth.loggingIn") : t("auth.loginBtn")}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
